/**
 * Entity Map — يحدد نوع الكيان من URL ويُدير القراءة/الكتابة المحلية
 *
 * المسؤوليات:
 * - تحديد entityType من URL (customer, product, sale, ...)
 * - تحديد جدول Dexie المقابل
 * - تحديد إذا الـ URL list أو single أو nested (مثل customer/{id}/sales)
 */
import { offlineDB } from './db';

// التعيين: entityType → جدول Dexie + URL patterns
export const ENTITY_TYPES = {
  customer: { table: 'local_customers', listRegex: /\/api\/customers\/?(\?|$)/, singleRegex: /\/api\/customers\/([^/?]+)\/?$/ },
  product:  { table: 'local_products',  listRegex: /\/api\/products\/?(\?|$)/,  singleRegex: /\/api\/products\/([^/?]+)\/?$/ },
  sale:     { table: 'local_sales',     listRegex: /\/api\/sales\/?(\?|$)/,     singleRegex: /\/api\/sales\/([^/?]+)\/?$/ },
  supplier: { table: 'local_suppliers', listRegex: /\/api\/suppliers\/?(\?|$)/, singleRegex: /\/api\/suppliers\/([^/?]+)\/?$/ },
  purchase: { table: 'local_purchases', listRegex: /\/api\/purchases\/?(\?|$)/, singleRegex: /\/api\/purchases\/([^/?]+)\/?$/ },
  // payments (موحّد customer + supplier)
  customer_payment: { table: 'local_payments', listRegex: /\/api\/customer-payments\/?(\?|$)/, singleRegex: /\/api\/customer-payments\/([^/?]+)\/?$/ },
  supplier_payment: { table: 'local_payments', listRegex: /\/api\/supplier-payments\/?(\?|$)/, singleRegex: /\/api\/supplier-payments\/([^/?]+)\/?$/ },
};

/** يستخرج entityType + entityId من URL (يدعم list أو single) */
export function detectEntity(url) {
  for (const [type, cfg] of Object.entries(ENTITY_TYPES)) {
    const sm = cfg.singleRegex.exec(url);
    if (sm) return { entityType: type, table: cfg.table, id: sm[1], scope: 'single' };
    if (cfg.listRegex.test(url)) return { entityType: type, table: cfg.table, id: null, scope: 'list' };
  }
  return null;
}

/** يولّد ID محلي مؤقت */
export function genOfflineId() {
  return `offline_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** يحفظ كياناً محلياً بـ flag pending — يُستخدم بعد POST أوفلاين.
 *  ملاحظة: نُخزّن _offline_pending كـ 1/0 (ليُفهرس بشكل صحيح في Dexie) */
export async function saveLocalEntity(entityType, data, opts = {}) {
  const cfg = ENTITY_TYPES[entityType];
  if (!cfg) return null;
  const id = data.id || genOfflineId();
  const record = {
    ...data,
    id,
    _offline_pending: opts.pending === false ? 0 : 1,
    _local_created_at: Date.now(),
  };
  await offlineDB[cfg.table].put(record);
  return record;
}

/** يحفظ كياناً قادم من السيرفر (seed أو بعد مزامنة) — pending: 0 */
export async function seedLocalEntity(entityType, data) {
  const cfg = ENTITY_TYPES[entityType];
  if (!cfg || !data || !data.id) return;
  await offlineDB[cfg.table].put({
    ...data,
    _offline_pending: 0,
    _local_synced_at: Date.now(),
  });
}

/** seed قائمة من السيرفر (نتيجة GET ناجح) */
export async function seedLocalList(entityType, items) {
  const cfg = ENTITY_TYPES[entityType];
  if (!cfg || !Array.isArray(items)) return;
  const records = items.filter(x => x && x.id).map(x => ({
    ...x,
    _offline_pending: 0,
    _local_synced_at: Date.now(),
  }));
  if (records.length) await offlineDB[cfg.table].bulkPut(records);
}

/** يحضر كيان محلياً (سواء أوفلاين أو مُزامن) */
export async function getLocalEntity(entityType, id) {
  const cfg = ENTITY_TYPES[entityType];
  if (!cfg) return null;
  return await offlineDB[cfg.table].get(id);
}

/** يحضر كل الـ pending offline لجدول معين */
export async function getPendingLocals(entityType) {
  const cfg = ENTITY_TYPES[entityType];
  if (!cfg) return [];
  return await offlineDB[cfg.table].where('_offline_pending').equals(1).toArray();
}

/** بعد المزامنة: نقل الكيان من ID مؤقت إلى ID حقيقي */
export async function rewriteLocalId(entityType, oldId, newId, mergedData = {}) {
  const cfg = ENTITY_TYPES[entityType];
  if (!cfg) return;
  const existing = await offlineDB[cfg.table].get(oldId);
  if (existing) await offlineDB[cfg.table].delete(oldId);
  await offlineDB[cfg.table].put({
    ...(existing || {}),
    ...mergedData,
    id: newId,
    _offline_pending: 0,
    _local_synced_at: Date.now(),
  });
}

/** يدمج server list + local pending */
export async function mergeListWithPending(entityType, serverList) {
  const list = Array.isArray(serverList) ? [...serverList] : [];
  const pendings = await getPendingLocals(entityType);
  const seenIds = new Set(list.map(x => x.id));
  for (const p of pendings) {
    if (!seenIds.has(p.id)) list.push(p);
  }
  return list;
}
